import { supabase } from '../lib/supabase';
import { notificationService } from './notificationService';
import { DEMO_ERROR_MESSAGE, isDemoUser } from '../utils/demoMode';

const STORAGE_BUCKET = 'worker-certifications';

// Demo-mode write paths used to return a fake-success { data: {...}, error: null }
// shape, which silently misled the user — the global DemoModeToast helps but
// modals would still close as if the save succeeded, and optimistic list
// removals on delete would run against rows that hadn't actually deleted
// (DNllfNne). The four write functions now return an explicit error so each
// caller's existing error path shows the message inline; the dispatch is kept
// so the system-level DemoModeToast still fires. The shared helper + message
// live in utils/demoMode (RczeIfwZ), so all services stay in lockstep.

export const workerCertificationService = {
  /**
   * Add a new certification record
   */
  async addCertification(data) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Demo mode guard
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const { data: result, error } = await supabase
        ?.from('worker_certifications')
        ?.insert({
          worker_user_id: data?.workerUserId,
          company_id: data?.companyId,
          certificate_type: data?.certificateType,
          certificate_number: data?.certificateNumber || null,
          expiry_date: data?.expiryDate,
          file_path: data?.filePath || null,
          uploaded_by: user?.id,
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Check expiry and notify admins
      if (result?.expiry_date && result?.company_id) {
        const today = new Date();
        today?.setHours(0, 0, 0, 0);
        const expiry = new Date(result?.expiry_date);
        expiry?.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
          notificationService?.notifyCertExpired(result?.worker_user_id, result?.certificate_type, result?.company_id);
        } else if (diffDays <= 30) {
          notificationService?.notifyCertExpiring(result?.worker_user_id, result?.certificate_type, result?.expiry_date, result?.company_id);
        }
      }

      return { data: result, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Get all certifications for a company (admin/site_manager)
   */
  async getCertificationsForCompany(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('worker_certifications')
        ?.select(`
          *,
          worker:user_profiles!worker_certifications_worker_user_id_fkey(
            id, full_name, email, role
          ),
          uploader:user_profiles!worker_certifications_uploaded_by_fkey(
            id, full_name
          )
        `)
        ?.eq('company_id', companyId)
        ?.order('expiry_date', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get certifications for a specific worker
   */
  async getCertificationsForWorker(workerId) {
    try {
      const { data, error } = await supabase
        ?.from('worker_certifications')
        ?.select(`
          *,
          worker:user_profiles!worker_certifications_worker_user_id_fkey(
            id, full_name, email
          )
        `)
        ?.eq('worker_user_id', workerId)
        ?.order('expiry_date', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Update an existing certification
   */
  async updateCertification(id, data) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const updatePayload = {};
      if (data?.certificateType !== undefined) updatePayload.certificate_type = data?.certificateType;
      if (data?.certificateNumber !== undefined) updatePayload.certificate_number = data?.certificateNumber;
      if (data?.expiryDate !== undefined) updatePayload.expiry_date = data?.expiryDate;
      if (data?.filePath !== undefined) updatePayload.file_path = data?.filePath;

      const { data: result, error } = await supabase
        ?.from('worker_certifications')
        ?.update(updatePayload)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      // Check expiry and notify admins after update
      if (result?.expiry_date && result?.company_id) {
        const today = new Date();
        today?.setHours(0, 0, 0, 0);
        const expiry = new Date(result?.expiry_date);
        expiry?.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
          notificationService?.notifyCertExpired(result?.worker_user_id, result?.certificate_type, result?.company_id);
        } else if (diffDays <= 30) {
          notificationService?.notifyCertExpiring(result?.worker_user_id, result?.certificate_type, result?.expiry_date, result?.company_id);
        }
      }

      return { data: result, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Delete a certification record
   */
  async deleteCertification(id) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const { error } = await supabase
        ?.from('worker_certifications')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Upload a certificate file to Supabase Storage
   */
  async uploadCertificateFile(file, workerId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${workerId}/${Date.now()}_${Math.random()?.toString(36)?.substring(2)}.${fileExt}`;

      const { data, error } = await supabase?.storage
        ?.from(STORAGE_BUCKET)
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;
      return { data: { path: data?.path }, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Get a signed URL for a certificate file
   */
  async getCertificateFileUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(STORAGE_BUCKET)
        ?.createSignedUrl(filePath, 3600);

      if (error) throw error;
      return { data: data?.signedUrl, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Compute expiry status for a certification
   */
  getExpiryStatus(expiryDate) {
    if (!expiryDate) return 'unknown';
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry?.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'expired';
    if (diffDays <= 30) return 'expiring_soon';
    return 'valid';
  },

  /**
   * Format expiry date as DD/MM/YYYY
   */
  formatExpiryDate(dateStr) {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  },
};
